/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';

import { useSpring, animated, easings } from 'react-spring';
import ResizeObserver from 'react-resize-observer';
import moment from 'moment';
import Checkbox from '@material-ui/core/Checkbox';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import IcomoonReact from 'icomoon-react';
import Select from 'react-select';

import checkboxTheme from '../../../shared/theme/checkboxTheme';
import {
  TabLabel,
  buttonReset,
  focusFill,
  focusFillLabel,
  focusRing,
  focusWithinRing,
} from '../../../shared/theme/focusStyles';
import { FOCUSABLE_SELECTOR, handleCheckboxEnter } from '../../../shared/utils/focusHelpers';
import { subscribeKeyboardNav } from '../../../shared/utils/keyboardNavMode';
import TBDatePicker from '../../../shared/components/TBDatePicker/TBDatePicker';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import SimpleCalendarItem from '../components/presentational/SimpleCalendarItem';
import Spinner from '../../../shared/components/Spinner/Spinner';
import SimpleCalendarSelectAthleteList from './SimpleCalendarSelectAthleteList';

import Text from '../../../shared/components/Text/Text';
import {
  copyWorkoutItems,
} from '../ducks/calendarActions';

// Create custom SVG icons for the checkboxes
const CustomUncheckedIcon = () => {
  const { colors } = useTheme();
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' stroke={colors.borderLight} />
    </svg>
  );
};

const CustomCheckedIcon = () => {
  const { colors } = useTheme();
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect x='0.5' y='0.5' width='17' height='17' rx='1.5' fill={colors.surface} stroke={colors.hover} />
      <path d='M5 9L8 12L13 6' stroke={colors.hover} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
};

// First, add a styled Checkbox component with specific styling
const StyledCheckbox = styled(Checkbox)`
  padding: 4px;

  &:hover {
    background-color: rgba(119, 167, 255, 0.04);
  }
`;

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: ${(props) => (props.hideOverflow ? 'hidden' : 'visible')};

  /* Phones: suppress the date-picker calendar popup - the on-screen
     keyboard covers it anyway, and typing the date is the more useful
     interaction at this size. The input itself still works. Scoped to
     this modal so date pickers elsewhere are unaffected. */
  @media (max-width: 480px) {
    .react-datepicker-popper {
      display: none !important;
    }
  }
`;

const SelectedItemsWarning = styled('div')`
  display: flex;
  gap: 12px;
  background: ${({ theme }) => theme.colors.warningSurface};
  border: 1px solid ${({ theme }) => theme.colors.warningBorder};
  border-radius: 8px;
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 11px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.warningText};
  p {
    font-size: 11pt;
    color: ${({ theme }) => theme.colors.warningText};
  }
`;

const WarningIconWrapper = styled('div')`
min-width: 18px;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;
`;

const PrimaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 35px;
  background:${(props) => (props.disabled ? props.theme.colors.buttonDisabledBg : props.theme.colors.buttonPrimaryBg)};
  color: ${(props) => (props.disabled ? props.theme.colors.buttonDisabledText : props.theme.colors.white)};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  border: ${(props) => (props.disabled ? `1px solid ${props.theme.colors.buttonDisabledBg}` : `1px solid ${props.theme.colors.buttonPrimaryBg}`)};
  margin-bottom: 8px;
  ${focusRing}
`;

const SecondaryButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 35px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  user-select: none;
  border: 1px solid ${({ theme }) => theme.colors.text};
  ${focusRing}
`;

const DateLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 8px;
  margin-top: ${(props) => (props.addTopMargin ? '20px' : '0')};
  display: block;
`;

const DatePickerRow = styled('div')`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`;

const TextAndPicker = styled('div')`
  display: flex;
  flex-direction: column;
  width: ${(props) => (props.fullWidth ? '100%' : '48%')};
`;

const DayCountText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: right;
  margin-top: 16px;
`;

// Add necessary styled components for slide 3
const OptionsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 0 4px;
`;

const CheckboxContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const CheckboxLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  margin-bottom: 0px;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const DaySelectors = styled('div')`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const DaySelector = styled('button')`
  ${buttonReset}
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? props.theme.colors.buttonPrimaryBg : props.theme.colors.surface)};
  color: ${(props) => (props.selected ? props.theme.colors.white : props.theme.colors.text)};
  border: 1px solid ${(props) => (props.selected ? props.theme.colors.buttonPrimaryBg : props.theme.colors.borderMedium)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text};
  }
  ${focusRing}
`;

// Add day selector components
const SelectDaysLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-top: 24px;
  margin-bottom: 16px;
`;

// Add styles for the scrollable calendar/athlete list
const CalendarsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 300px;
  overflow-y: auto;
  margin-bottom: 24px;
  
  /* Hide the default scrollbar track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scrollbarThumb};
    border-radius: 3px;
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollbarThumb} transparent;
`;

// Calendar renderer for react-window
// Defined outside component to avoid re-creation on each render
const CalendarRenderer = ({ data, index, style }) => {
  const { calendars, toggleSelection, selected } = data;
  const calendar = calendars[index];

  // Make sure we have a unique ID for each calendar
  const calendarId = calendar.id || `calendar-${index}`;

  if (calendar.childCalendars) {
    // Parent calendar
    return (
      <CalendarItem style={style}>
        <ThemeProvider theme={checkboxTheme}>
          <StyledCheckbox
            checked={selected[calendarId] || false}
            onChange={() => toggleSelection(calendarId)}
            icon={<CustomUncheckedIcon />}
            checkedIcon={<CustomCheckedIcon />}
            color='default'
          />
        </ThemeProvider>
        <CalendarItemText>{calendar.name}</CalendarItemText>
      </CalendarItem>
    );
  }
  // Sub calendar or searched calendar
  return (
    <SubCalendarItem style={style}>
      <ThemeProvider theme={checkboxTheme}>
        <StyledCheckbox
          checked={selected[calendarId] || false}
          onChange={() => toggleSelection(calendarId)}
          icon={<CustomUncheckedIcon />}
          checkedIcon={<CustomCheckedIcon />}
          color='default'
        />
      </ThemeProvider>
      <CalendarItemText>{calendar.name}</CalendarItemText>
    </SubCalendarItem>
  );
};

CalendarRenderer.propTypes = {
  data: PropTypes.shape({
    calendars: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      childCalendars: PropTypes.arrayOf(PropTypes.any),
    })).isRequired,
    toggleSelection: PropTypes.func.isRequired,
    selected: PropTypes.objectOf(PropTypes.bool).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  style: PropTypes.shape({}).isRequired,
};

// Athlete renderer for react-window
// Defined outside component to avoid re-creation on each render
const AthleteRenderer = ({ data, index, style }) => {
  const { athletes, toggleSelection, selected } = data;
  const athlete = athletes[index];

  // Make sure we have a unique ID for each athlete
  const athleteId = athlete.userId || `athlete-${index}`;

  return (
    <AthleteItem style={style}>
      <ThemeProvider theme={checkboxTheme}>
        <StyledCheckbox
          checked={selected[athleteId] || false}
          onChange={() => toggleSelection(athleteId)}
          icon={<CustomUncheckedIcon />}
          checkedIcon={<CustomCheckedIcon />}
          color='default'
        />
      </ThemeProvider>
      {athlete.pic && (
        <AthleteAvatar>
          <img src={athlete.pic} alt='' />
        </AthleteAvatar>
      )}
      <AthleteName>{`${athlete.first} ${athlete.last}`}</AthleteName>
    </AthleteItem>
  );
};

AthleteRenderer.propTypes = {
  data: PropTypes.shape({
    athletes: PropTypes.arrayOf(PropTypes.shape({
      userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      first: PropTypes.string.isRequired,
      last: PropTypes.string.isRequired,
      pic: PropTypes.string,
    })).isRequired,
    toggleSelection: PropTypes.func.isRequired,
    selected: PropTypes.objectOf(PropTypes.bool).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  style: PropTypes.shape({}).isRequired,
};

const CalendarItem = styled('div')`
  display: flex;
  align-items: center;
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const CalendarItemText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const SubCalendarItem = styled('div')`
  display: flex;
  align-items: center;
  padding-left: 20px;
  width: 100%;
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const AthleteItem = styled('div')`
  display: flex;
  align-items: center;
  gap: 0;
  height: 30px;
  padding: 10px;
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const AthleteAvatar = styled('div')`
  img {
    height: 30px;
    width: 30px;
    border-radius: 50%;
    margin-right: 10px;
  }
  display: flex;
  align-items: center;
`;

const AthleteName = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

// New styled components for the updated first slide
const TabsContainer = styled('div')`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderMedium};
  margin-bottom: 20px;
`;

const DateModeTab = styled('button')`
  ${buttonReset}
  padding: 15px 20px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: ${(props) => (props.active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: ${(props) => (props.active ? props.theme.colors.link : props.theme.colors.textSecondary)};
  border-bottom: ${(props) => (props.active ? `3px solid ${props.theme.colors.link}` : '3px solid transparent')};
  margin-bottom: -1px;
  ${focusFillLabel}
`;

const SelectDateSection = styled('div')`
  margin-bottom: 12px;
`;

/* Wraps only the date field so the focus border hugs the input and its calendar icon,
   rather than enclosing the "Start Date" / "End Date" label sitting above it. The gap under
   that label lives here rather than on the picker: this is a flex item, so it establishes a
   BFC and a child's margin would sit inside the border instead of above it. */
const PickerFocusWrap = styled('div')`
  position: relative;
  margin-top: ${(props) => (props.addTopMargin ? '8px' : '0')};
  ${focusWithinRing}
`;

const StatsContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  margin: 16px 0;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: 10px;
`;

const CheckboxRow = styled('div')`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
`;

const AdditionalOptionsToggle = styled('button')`
  ${buttonReset}
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  /* Breathing room so the focus fill sits around the text rather than tight against it.
     The negative margin cancels it, leaving the resting layout unchanged -- the same trick
     SwapPropDropdown uses for its focus highlight. */
  padding: 5px;
  margin: -5px;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  ${focusFill}
`;

const ChevronIcon = styled('span')`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

// Add toggle components from SelectCalendarModal
const ToggleContainer = styled('div')`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderMedium};
`;

const LeftToggle = styled('button')`
  ${buttonReset}
  padding: 15px 20px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: ${(props) => (props.showLine ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: ${(props) => (props.showLine ? props.theme.colors.link : props.theme.colors.textSecondary)};
  border-bottom: ${(props) => (props.showLine ? `3px solid ${props.theme.colors.link}` : '3px solid transparent')};
  margin-bottom: -1px;
  ${focusFillLabel}
`;

const RightToggle = styled('button')`
  ${buttonReset}
  padding: 15px 20px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: ${(props) => (props.showLine ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: ${(props) => (props.showLine ? props.theme.colors.link : props.theme.colors.textSecondary)};
  border-bottom: ${(props) => (props.showLine ? `3px solid ${props.theme.colors.link}` : '3px solid transparent')};
  margin-bottom: -1px;
  ${focusFillLabel}
`;

const SearchBarContainer = styled('div')`
  margin-bottom: 15px;
  border: 2px solid ${({ theme }) => theme.colors.borderMedium};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surfaceInput};
  height: 40px;
  width: 100%;
  display: flex;
  
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.hover};
  }
`;

const IconContainer = styled('div')`
  height: 100%;
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchBar = styled('input')`
  width: 100%;
  border: none;
  font-family: 'Nunito Sans';
  font-size: 14px;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

// Add breadcrumb components
const BreadcrumbContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  margin: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  gap: 8px;

  @media (max-width: 768px) {
    padding: 0px 12px;
    padding-bottom: 12px;
    gap: 4px;
  }

  /* Phones: the modal's content padding already insets the breadcrumbs;
     drop the extra horizontal padding to give the labels room. */
  @media (max-width: 480px) {
    padding: 0px;
    padding-bottom: 12px;
  }
`;

// Selection container for calendar/athlete lists
const SelectedCalendarCheckboxContainer = styled('div')`
  display: flex;
  background: ${({ theme }) => theme.colors.surface};
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  padding-top: 8px;
  padding-left: 2px;
  padding-right: 2px;
  width: fit-content;
  border-radius: 4px;
  margin-left: 7px;
  position: ${(props) => (props.itemsAreSelected && 'sticky')};
  top: ${(props) => (props.itemsAreSelected && '10px')};
  z-index: ${(props) => (props.itemsAreSelected && 100)};

  .checkbox-input {
    appearance: none; /* Remove default checkbox styling */
    min-width: 18px;
    min-height: 18px;
    max-width: 18px;
    max-height: 18px;
    border: 2px solid ${(props) => (props.isChecked ? props.theme.colors.hover : props.theme.colors.borderMedium)};
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    position: relative;
  }

  /* The appearance:none above strips the native focus ring, and global.scss:43 kills the
     outline, so tabbing here landed on a control with no indicator at all -- it read as the
     Tab having done nothing. */
  .checkbox-input:focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.hover};
  }

  .checkbox-input:checked::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 5px;
    width: 5px;
    height: 10px;
    border: solid ${(props) => (props.isChecked ? props.theme.colors.hover : props.theme.colors.borderMedium)};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: block;
  }

 .checkbox-input:indeterminate {
    border-color: ${({ theme }) => theme.colors.hover}
  }
  .checkbox-input:indeterminate::after {
    content: '-';
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 31px;
    color: ${({ theme }) => theme.colors.hover};
    margin-top: 3px;
  }
`;

const SelectionText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${(props) => (props.hasSelection ? props.theme.colors.hover : props.theme.colors.textSecondary)};
  font-weight: ${(props) => (props.hasSelection ? '700' : '400')};
`;

const BreadcrumbStep = styled('button')`
  ${buttonReset}
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};

  @media (max-width: 768px) {
    gap: 6px;
  }
  ${focusRing}
`;

const BreadcrumbNumber = styled('span')`
  min-width: 44px;
  min-height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans';
  font-size: 18px;
  font-weight: 600;
  background: ${(props) => (props.completed ? '#0DCC8A' : props.theme.colors.surface)};
  color: ${(props) => (props.completed ? props.theme.colors.white : props.theme.colors.text)};
  border: ${(props) => {
    if (props.completed) return 'none';
    if (props.active) return '2px solid #0DCC8A';
    return '2px solid #757575';
  }};
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    min-width: 36px;
    min-height: 36px;
    font-size: 14px;
  }

  /* Phones: drop the numbered bubbles entirely - they push the
     breadcrumbs off-screen. Step state is still conveyed by the
     label font weight. */
  @media (max-width: 480px) {
    display: none;
  }
`;

const BreadcrumbText = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: ${(props) => (props.active || props.completed ? '600' : '400')};
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 11px;
  }

  /* Phones: with the bubbles hidden the labels are all that's left -
     let them wrap so all three steps fit the modal width. */
  @media (max-width: 480px) {
    white-space: normal;
  }
`;

const BreadcrumbArrow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 4px;
  transform: rotate(-90deg);
`;

/* Add expandable section for items to copy. `visibility` is what keeps the collapsed
   checkboxes out of the tab order -- max-height alone leaves them focusable. It is delayed
   by the collapse duration so the existing animation looks unchanged. */
const ItemsToCopySection = styled('div')`
  max-height: ${(props) => (props.expanded ? '1000px' : '0px')};
  overflow: hidden;
  visibility: ${(props) => (props.expanded ? 'visible' : 'hidden')};
  transition: max-height 0.3s ease,
    visibility 0s linear ${(props) => (props.expanded ? '0s' : '0.3s')};
  margin-bottom: ${(props) => (props.expanded ? '24px' : '0px')};
`;

const ItemsToCopyTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 16px;
  margin-top: 8px;
`;

// Styled components for placement and existing workouts section
const BottomOptionsContainer = styled('div')`
  display: flex;
  gap: 24px;
  margin-top: 24px;
`;

const OptionsColumn = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const OptionLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 8px;
`;

const SelectContainer = styled('div')`
  .react-select__control {
    background-color: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: 4px;
    min-height: 40px;
    box-shadow: none;

    &:hover {
      border-color: ${({ theme }) => theme.colors.text};
    }

    &--is-focused {
      border-color: ${({ theme }) => theme.colors.text};
      box-shadow: none;
    }
  }

  /* react-select's own focus ring is killed by the box-shadow: none above, so give the
     control the same blue border as every other field. Written at the top level, not nested,
     because the attribute sits on an ancestor of this element. Gated on keyboard nav because
     the focusable element react-select renders is an <input>, and :focus-visible matches
     those even for mouse clicks. */
  [data-nav-mode='keyboard'] & .react-select__control--is-focused {
    border-color: ${({ theme }) => theme.colors.hover};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.hover};
  }

  .react-select__value-container {
    padding: 2px 12px;
  }

  .react-select__single-value {
    color: ${({ theme }) => theme.colors.text};
    font-family: 'Nunito Sans';
    font-size: 14px;
  }

  .react-select__menu {
    background-color: ${({ theme }) => theme.colors.surface};
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  .react-select__option {
    font-family: 'Nunito Sans';
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;
    
    &--is-selected {
      background-color: ${({ theme }) => theme.colors.surfaceSecondary};
      color: ${({ theme }) => theme.colors.text};
    }

    &--is-focused {
      background-color: ${({ theme }) => theme.colors.surfaceSecondary};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.surfaceSecondary};
    }
  }
`;

// Styled components for week/day selection (Program Library)
const WeekDayContainer = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-bottom: 24px;
  background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  padding: 16px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  overflow: scroll;
`;

const WeekRow = styled('div')`
  display: flex;
  flex-direction: column;
`;

const WeekCheckboxRow = styled('div')`
  display: flex;
  align-items: center;
`;

const WeekLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  margin-bottom: 0px;
  min-width: 50px;
`;

const DayCheckboxRow = styled('div')`
  display: flex;
  align-items: center;
  margin-left: 0px;
  margin-top: 8px;
`;

const DayLabel = styled('label')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  margin-bottom: 0px;
  min-width: 50px;
`;

const ProgramNameDisplay = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 24px;
`;

/**
 * The Placement dropdown. Rendered on two different steps with the same state, so it lives
 * here as one component rather than being duplicated.
 *
 * react-select 2.x opens on Space and the arrow keys but deliberately ignores Enter while
 * closed, so Enter is added here. It reads menuIsOpen back off the component, which means
 * open state has to be controlled.
 */
const PlacementSelect = ({ options, value, onChange }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isMenuOpen) {
      /* react-select checks defaultPrevented before running its own handler, so this cleanly
         takes over rather than racing it. */
      e.preventDefault();
      setIsMenuOpen(true);
    }
  };

  return (
    <SelectContainer>
      <Select
        options={options}
        value={value}
        onChange={(option) => {
          onChange(option);
          setIsMenuOpen(false);
        }}
        className='react-select'
        classNamePrefix='react-select'
        isSearchable={false}
        menuIsOpen={isMenuOpen}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        onKeyDown={handleKeyDown}
      />
    </SelectContainer>
  );
};

PlacementSelect.propTypes = {
  options: PropTypes.instanceOf(Array).isRequired,
  value: PropTypes.instanceOf(Object).isRequired,
  onChange: PropTypes.func.isRequired,
};

const CopyWorkoutModal = ({ closeModal, modalData }) => {
  const preSavedItemAssignedIds = Object.values(modalData?.preSelectedItems || {})
    .map((item) => item?.assignedId)
    .filter((id) => typeof id === 'string');

  const savedItemAssignedIds = Object.values(modalData?.preSelectedItems || {})
    .map((item) => item?.assignedId)
    .filter((id) => typeof id === 'number');

  // State for slide management
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [hideOverflow, setHideOverflow] = useState(true);

  const dispatch = useDispatch();
  const { colors } = useTheme();

  // Helper to extract date from pre-selected items
  const getDateFromPreSelectedItems = () => {
    if (modalData?.preSelectedItems) {
      const firstItem = Object.values(modalData.preSelectedItems)[0];
      return firstItem?.date; // Returns "2025-10-14" format
    }
    return null;
  };

  // Initialize form data based on modalData from week view or pre-selected items
  const initializeDatesFromModalData = () => {
    // Check for pre-selected items first
    if (modalData?.preSelectedItems) {
      const preSelectedDate = getDateFromPreSelectedItems();
      if (preSelectedDate) {
        return {
          dateMode: 'single',
          startDate: moment(preSelectedDate).toDate(),
          endDate: moment(preSelectedDate).toDate(),
          startDateString: preSelectedDate,
          endDateString: preSelectedDate,
        };
      }
    }

    // Check for week view data
    if (modalData?.isFromWeekView && modalData?.weekStartDate && modalData?.weekEndDate) {
      return {
        dateMode: 'range',
        startDate: modalData.weekStartDate,
        endDate: modalData.weekEndDate,
        startDateString: moment(modalData.weekStartDate).format('YYYY-MM-DD'),
        endDateString: moment(modalData.weekEndDate).format('YYYY-MM-DD'),
      };
    }

    // Default - use currentViewedDate from modalData, fallback to today
    const defaultDate = modalData?.currentViewedDate
      ? moment(modalData.currentViewedDate).toDate()
      : new Date();

    return {
      dateMode: 'single',
      startDate: defaultDate,
      endDate: defaultDate,
      startDateString: moment(defaultDate).format('YYYY-MM-DD'),
      endDateString: moment(defaultDate).format('YYYY-MM-DD'),
    };
  };

  const initialDates = initializeDatesFromModalData();

  // State for form data
  const [dateMode, setDateMode] = useState(initialDates.dateMode); // 'single' or 'range'
  const [secondSlideDateMode, setSecondSlideDateMode] = useState('single'); // 'single' or 'range'
  const [startDate, setStartDate] = useState(initialDates.startDate);
  const [endDate, setEndDate] = useState(initialDates.endDate);
  // String versions for TBDatePicker
  const [startDateString, setStartDateString] = useState(initialDates.startDateString);
  const [endDateString, setEndDateString] = useState(initialDates.endDateString);
  const [selectedDays, setSelectedDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });

  // State for second slide date management
  const defaultDate = modalData?.currentViewedDate
    ? moment(modalData.currentViewedDate).toDate()
    : new Date();

  const [secondSlideStartDate, setSecondSlideStartDate] = useState(defaultDate);
  const [secondSlideEndDate, setSecondSlideEndDate] = useState(defaultDate);
  const [secondSlideStartDateString, setSecondSlideStartDateString] = useState(moment(defaultDate).format('YYYY-MM-DD'));
  const [secondSlideEndDateString, setSecondSlideEndDateString] = useState(moment(defaultDate).format('YYYY-MM-DD'));

  // State for slide 3
  const [contentOptions, setContentOptions] = useState({
    circuits: true,
    health: true,
    notes: true,
    rest: true,
    warmUps: true,
    cooldowns: true,
    questionnaires: true,
    sessions: true,
    videos: true,
    prescribedLoads: true,
    workoutTitles: false,
    clearCurrentWorkouts: false,
  });

  // Additional state for the missing checkboxes
  const [additionalOptions, setAdditionalOptions] = useState({
    circuits2: true,
    health2: true,
    notes2: true,
  });

  // State for additional options and items
  const [showAdditionalOptions, setShowAdditionalOptions] = useState(false);
  const [dateRangeError, setDateRangeError] = useState(null);

  // State for checkbox functionality
  const [checkedCalendars, setCheckedCalendars] = useState(new Set());
  const [checkedAthletes, setCheckedAthletes] = useState(new Set());

  // Get data from Redux store
  const parentCalendars = useSelector((state) => state.calendar.data.parentCalendars);
  const calendarSelectAthletes = useSelector((state) => state.calendar.data.calendarSelectAthletes);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const isParentCalendarsLoading = useSelector(
    (state) => state.calendar.ui.isParentCalendarsLoading,
  );
  // Program library context
  const programType = useSelector((state) => state.calendar.data.programType);
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const programName = useSelector((state) => state.calendar.data.programName);
  const nonDateBasedProgramItemsProgramId = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItemsProgramId,
  );

  // Helper to extract week/day from pre-selected items in program library
  const getWeekDayFromPreSelectedItems = () => {
    if (modalData?.preSelectedItems && programType === 'free-form-program') {
      // Check if source day metadata was provided in modalData
      if (modalData.sourceDayNumber !== undefined && modalData.sourceWeekNumber !== undefined) {
        return {
          weekNumber: modalData.sourceWeekNumber,
          dayNumber: modalData.sourceDayNumber,
        };
      }

      // Fallback: try to get from first item (existing logic)
      const firstItem = Object.values(modalData?.preSelectedItems)[0];
      if (firstItem?.weekNumber !== undefined && firstItem?.dayNumber !== undefined) {
        return {
          weekNumber: firstItem.weekNumber,
          dayNumber: firstItem.dayNumber,
        };
      }
    }
    return null;
  };

  // Create flattened calendar array (parent + child calendars in single list)
  const flattenedCalendars = parentCalendars
    .filter((calendar) => !calendar.archived)
    .flatMap((parentCalendar) => {
      const filteredChildren = parentCalendar.childCalendars
        .filter((child) => !child.archived)
        .map((child) => ({
          ...child,
          parentCalendarInfo: {
            id: parentCalendar.id,
            name: parentCalendar.name,
          },
        }));

      return [parentCalendar, ...filteredChildren];
    });

  // Calculate total days between start and end date (inclusive)
  const totalDaysCount = dateMode === 'single' ? 1 : (startDate && endDate
    ? moment(endDate).startOf('day').diff(moment(startDate).startOf('day'), 'days') + 1 : 0);

  /* A date field can legitimately be empty now (the user cleared it), but a copy needs a
     date -- so the step's forward button is disabled until one is set. The
     free-form-program branch has no date inputs at all. */
  const isMissingCopyDate = programType !== 'free-form-program'
    && (!startDateString || (dateMode === 'range' && !endDateString));

  const isMissingTargetDate = programType !== 'free-form-program'
    && (!secondSlideStartDateString
      || (secondSlideDateMode === 'range' && !secondSlideEndDateString));

  const isPasteDisabled = isMissingTargetDate
    || (secondSlideDateMode === 'range' && !Object.values(selectedDays).some(Boolean));

  // Date picker handlers that update both formats
  const handleStartDateChange = (date) => {
    const momentDate = moment(date);
    /* Clearing the input calls this with null. Formatting that would store the literal
       string 'Invalid date', which is truthy and crashes react-datepicker on the next
       render, so an empty field has to stay genuinely empty. */
    if (!date || !momentDate.isValid()) {
      setStartDate(null);
      setStartDateString('');
      return;
    }
    setStartDate(momentDate.toDate());
    setStartDateString(momentDate.format('YYYY-MM-DD'));

    // If end date is before the new start date, update end date to match start date
    if (moment(endDate).isBefore(momentDate, 'day')) {
      setEndDate(momentDate.toDate());
      setEndDateString(momentDate.format('YYYY-MM-DD'));
    }
  };

  const handleEndDateChange = (date) => {
    const momentDate = moment(date);
    if (!date || !momentDate.isValid()) {
      setEndDate(null);
      setEndDateString('');
      return;
    }
    setEndDate(momentDate.toDate());
    setEndDateString(momentDate.format('YYYY-MM-DD'));
  };

  /* The date fields and the placement dropdown must only show a focus ring for keyboard
     users, and neither :focus-within nor :focus-visible can tell mouse from keyboard on an
     input -- so mirror the tracked modality onto the container and let CSS key off it.
     Written imperatively via the ref rather than state: this fires on every keystroke and
     click, and re-rendering this component that often would be wasteful. */
  const modalContainerRef = useRef(null);

  useEffect(() => subscribeKeyboardNav((usingKeyboard) => {
    if (modalContainerRef.current) {
      modalContainerRef.current.dataset.navMode = usingKeyboard ? 'keyboard' : 'mouse';
    }
  }), []);

  /* Slide content refs, used to move focus into the step being shown. Only one slide is
     mounted at a time, so exactly one of these is non-null. */
  const slide1Ref = useRef(null);
  const slide2Ref = useRef(null);
  const slide3Ref = useRef(null);

  const getActiveSlide = () => [slide1Ref, slide2Ref, slide3Ref]
    .map((ref) => ref.current)
    .find(Boolean);

  /* Moves focus to the first control after a date picker, so finishing with one date hands
     the user straight to the next field. Date-picker inputs are deliberately not skipped:
     landing on End Date with its calendar open is the point. */
  const focusFieldAfter = (wrapper) => {
    const container = getActiveSlide();
    if (!container) {
      return;
    }
    /* The footer is deliberately included: on the target-date step the date field is the last
       input, so PASTE is the next field. That cannot cause an accidental submit -- the day
       cell consumes the Enter keydown, so activating the button needs a second, separate
       press. */
    const next = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
      .find((el) => !wrapper.contains(el)
        // eslint-disable-next-line no-bitwise
        && (wrapper.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));
    next?.focus();
  };

  /* Only one date picker can be open at a time, so a single flag covers all of them. */
  const escapeBelongsToDatePickerRef = useRef(false);

  /* react-datepicker calls preventDefault on Escape but never stopPropagation, and its
     calendar popper renders inline inside the react-modal content element -- so an Escape
     meant for the calendar also reaches react-modal's own onKeyDown and closes the entire
     modal, discarding the user's copy configuration. We snapshot "is the calendar open"
     during the capture phase, before react-datepicker's handler runs and closes it, then
     stop propagation on the way back up. Reading the open state in the bubble handler alone
     would depend on React's setState batching timing. */
  const handleDatePickerKeyDownCapture = (e) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      escapeBelongsToDatePickerRef.current = !!e.currentTarget.querySelector('.react-datepicker-popper');
    }
  };

  const handleDatePickerKeyDown = (e) => {
    if (!escapeBelongsToDatePickerRef.current) {
      return;
    }
    if (e.key === 'Escape') {
      escapeBelongsToDatePickerRef.current = false;
      e.stopPropagation();
      // Escaping out of a calendar should hand the user on, not strand them on a closed field.
      focusFieldAfter(e.currentTarget);
    }
    if (e.key === 'Enter') {
      // react-datepicker has already applied the date and closed the calendar by now.
      escapeBelongsToDatePickerRef.current = false;
      focusFieldAfter(e.currentTarget);
    }
  };

  const hasNavigatedRef = useRef(false);

  /* On a step change this stops focus falling to <body> when the focused button unmounts,
     which would make the next Tab restart from the top of the document.

     Deliberately skipped on open: nothing should be focused until the user presses Tab,
     and because the breadcrumb is not tabbable the first Tab lands on the Single Date tab
     on its own. */
  useEffect(() => {
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      return;
    }

    const container = getActiveSlide();
    if (!container) {
      return;
    }

    const target = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
      // Skip the footer so Enter can't immediately re-submit, and skip date-picker inputs,
      // which pop their calendar open on focus.
      .find((el) => !el.closest('[data-modal-footer]') && !el.classList.contains('date-picker'));

    /* preventScroll because this runs while ModalContainer is overflow: hidden for the
       300ms slide transition, and native focus scrolling walks clipped ancestors too --
       it would displace the modal's contents with no way for the user to scroll back.
       The target is the first control of a fresh step, so it is already in view. */
    (target || container).focus({ preventScroll: true });
  }, [currentSlide]);

  /* Enter on a date-mode tab commits the mode and moves focus into that tab's panel. The
     mode switch swaps the panel's contents (one picker vs. two), so focus has to wait for
     the next render -- hence a counter rather than focusing inline. */
  const [panelFocusRequest, setPanelFocusRequest] = useState(0);
  const [panelFocusTargetId, setPanelFocusTargetId] = useState(null);

  useEffect(() => {
    if (!panelFocusRequest || !panelFocusTargetId) {
      return;
    }
    const panel = document.getElementById(panelFocusTargetId);
    panel?.querySelector(FOCUSABLE_SELECTOR)?.focus();
  }, [panelFocusRequest, panelFocusTargetId]);

  const handleDateModeTabKeyDown = (e, setMode, mode, panelId) => {
    if (e.key !== 'Enter') {
      return;
    }
    /* preventDefault suppresses the native Enter-to-click so the mode isn't set twice.
       Tab is deliberately left alone: it just continues through the current page. Space
       still selects via onClick without moving focus. */
    e.preventDefault();
    setMode(mode);
    setPanelFocusTargetId(panelId);
    setPanelFocusRequest((n) => n + 1);
  };

  /* Expanding Additional Options moves focus into the panel; collapsing hands it back to
     the toggle, which would otherwise be lost when the panel becomes visibility: hidden. */
  const additionalOptionsToggleRef = useRef(null);
  const [optionsFocusRequest, setOptionsFocusRequest] = useState(0);

  useEffect(() => {
    if (!optionsFocusRequest) {
      return;
    }
    const panel = document.getElementById('copy-additional-options');
    panel?.querySelector(FOCUSABLE_SELECTOR)?.focus();
  }, [optionsFocusRequest]);

  const handleAdditionalOptionsToggle = () => {
    const willExpand = !showAdditionalOptions;
    setShowAdditionalOptions(willExpand);
    if (willExpand) {
      setOptionsFocusRequest((n) => n + 1);
    } else {
      additionalOptionsToggleRef.current?.focus();
    }
  };

  // Helper function to toggle day selection
  const toggleDay = (day) => {
    setSelectedDays({
      ...selectedDays,
      [day]: !selectedDays[day],
    });
  };

  // Date change handlers for second slide
  const handleSecondSlideStartDateChange = (date) => {
    const momentDate = moment(date);
    if (!date || !momentDate.isValid()) {
      setSecondSlideStartDate(null);
      setSecondSlideStartDateString('');
      return;
    }
    setSecondSlideStartDate(momentDate.toDate());
    setSecondSlideStartDateString(momentDate.format('YYYY-MM-DD'));

    // If end date is before the new start date, update end date to match start date
    if (moment(secondSlideEndDate).isBefore(momentDate, 'day')) {
      setSecondSlideEndDate(momentDate.toDate());
      setSecondSlideEndDateString(momentDate.format('YYYY-MM-DD'));
    }
  };

  const handleSecondSlideEndDateChange = (date) => {
    const momentDate = moment(date);
    if (!date || !momentDate.isValid()) {
      setSecondSlideEndDate(null);
      setSecondSlideEndDateString('');
      return;
    }
    setSecondSlideEndDate(momentDate.toDate());
    setSecondSlideEndDateString(momentDate.format('YYYY-MM-DD'));
  };

  // 365-day validation
  useEffect(() => {
    if (totalDaysCount > 365) {
      setDateRangeError('You cannot copy a date range longer than 365 days');
    } else {
      setDateRangeError(null);
    }
  }, [totalDaysCount]);

  // Helper function to toggle content options
  const toggleContentOption = (option) => {
    setContentOptions((prevOptions) => ({
      ...prevOptions,
      [option]: !prevOptions[option],
    }));
  };

  // Helper function to toggle additional options
  const toggleAdditionalOption = (option) => {
    setAdditionalOptions((prevOptions) => ({
      ...prevOptions,
      [option]: !prevOptions[option],
    }));
  };

  // Determine which slide to show based on mode
  // Skip first slide if: 1) skipFirstSlide is set OR
  // 2) in free-form-program with pre-selected items
  const shouldSkipFirstSlide = modalData?.skipFirstSlide
    || (programType === 'free-form-program' && modalData?.preSelectedItems);

  const showSlide1 = !shouldSkipFirstSlide && currentSlide === 0;
  const showSlide2 = shouldSkipFirstSlide ? currentSlide === 0 : currentSlide === 1;
  const showSlide3 = shouldSkipFirstSlide ? currentSlide === 1 : currentSlide === 2;

  // Animation for slide transitions
  const slide1Animation = useSpring({
    opacity: showSlide1 ? 1 : 0,
    transform: showSlide1 ? 'translateX(0%)' : 'translateX(-100%)',
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    immediate: isResizing,
    onRest: () => {
      // When animation completes, allow overflow if this slide is active
      if (showSlide1) {
        setHideOverflow(false);
      }
    },
  });

  // Calculate transform for slide 2
  let slide2Transform;
  if (showSlide2) {
    slide2Transform = 'translateX(0%)';
  } else if (currentSlide < 1) {
    slide2Transform = 'translateX(100%)';
  } else {
    slide2Transform = 'translateX(-100%)';
  }

  const slide2Animation = useSpring({
    opacity: showSlide2 ? 1 : 0,
    transform: slide2Transform,
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    immediate: isResizing,
    onRest: () => {
      // When animation completes, allow overflow if this slide is active
      if (showSlide2) {
        setHideOverflow(false);
      }
    },
  });

  // Calculate transform for slide 3
  let slide3Transform;
  if (showSlide3) {
    slide3Transform = 'translateX(0%)';
  } else if (currentSlide < 2) {
    slide3Transform = 'translateX(100%)';
  } else {
    slide3Transform = 'translateX(-100%)';
  }

  const slide3Animation = useSpring({
    opacity: showSlide3 ? 1 : 0,
    transform: slide3Transform,
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    immediate: isResizing,
    onRest: () => {
      // When animation completes, allow overflow if this slide is active
      if (showSlide3) {
        setHideOverflow(false);
      }
    },
  });

  // Handle next slide - goes through all slides sequentially
  const handleNext = () => {
    if (totalDaysCount > 365) {
      return;
    }

    // Can't advance off step 1 without the date the copy is built from.
    if (!shouldSkipFirstSlide && currentSlide === 0 && isMissingCopyDate) {
      return;
    }

    // Hide overflow during animation
    setHideOverflow(true);

    // When going from slide 1 to slide 2, set up the second slide based on first slide's dateMode
    if (!shouldSkipFirstSlide && currentSlide === 0) {
      if (dateMode === 'range') {
        // If range was selected on slide 1, lock slide 2 to single date mode
        setSecondSlideDateMode('single');
      }
      // If single was selected on slide 1, keep secondSlideDateMode as is (user can choose)
    }

    // For free-form-program with pre-selected items, we only have 1 slide (paste destination)
    const maxSlide = shouldSkipFirstSlide
      && programType === 'free-form-program'
      && modalData?.preSelectedItems
      ? 0 : (shouldSkipFirstSlide ? 1 : 2);

    if (currentSlide < maxSlide) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  // Handle back - goes through all 3 slides sequentially
  const handleBack = () => {
    // Hide overflow during animation
    setHideOverflow(true);

    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else {
      closeModal(); // Close modal when on first slide
    }
  };

  // Add refs
  const calendarContainerRef = useRef(null);
  // Add state for calendar and athlete selection from SelectCalendarModal
  const [rightToggled, setRightToggled] = useState(
    !!(selectedReduxAthlete && Object.keys(selectedReduxAthlete).length),
  );
  const [searchValue, setSearchValue] = useState('');
  const [isUserSearching, setIsUserSearching] = useState(false);
  const [isUserSearchingAthletes, setIsUserSearchingAthletes] = useState(false);
  const [searchedList, setSearchedList] = useState([]);
  const [searchedAthleteList, setSearchedAthleteList] = useState([]);

  // State for placement and existing workouts options on slide 3
  const [placementOption, setPlacementOption] = useState({ value: 'bottom', label: 'Bottom of workout' });
  const [clearExistingWorkouts, setClearExistingWorkouts] = useState(false);

  // State for program library week/day selections (Slide 1)
  const [selectedProgramDays, setSelectedProgramDays] = useState(new Set());
  // State for program library paste destination (Slide 2)
  const [pasteDestinationDays, setPasteDestinationDays] = useState(new Set());

  // Get available calendars for select all functionality
  const getAvailableCalendars = () => (isUserSearching ? searchedList : flattenedCalendars);

  // Get available athletes for select all functionality
  const getAvailableAthletes = () => (isUserSearchingAthletes
    ? searchedAthleteList.filter((athlete) => !athlete.fake)
    : calendarSelectAthletes);

  // Calculate select all/none states for calendars
  const availableCalendars = getAvailableCalendars();
  const allCalendarsSelected = availableCalendars.length > 0
    && availableCalendars.every((calendar) => checkedCalendars.has(calendar.id));
  const someCalendarsSelected = availableCalendars.some(
    (calendar) => checkedCalendars.has(calendar.id),
  )
    && !allCalendarsSelected;

  // Calculate select all/none states for athletes
  const availableAthletes = getAvailableAthletes();
  const allAthletesSelected = availableAthletes.length > 0
    && availableAthletes.every((athlete) => checkedAthletes.has(athlete.id));
  const someAthletesSelected = availableAthletes.some(
    (athlete) => checkedAthletes.has(athlete.id),
  )
    && !allAthletesSelected;

  // Handle select all/none for calendars
  const handleCalendarSelectAll = (event) => {
    event.stopPropagation();

    if (allCalendarsSelected) {
      // Unselect all calendars
      const newSet = new Set(checkedCalendars);
      availableCalendars.forEach((calendar) => {
        newSet.delete(calendar.id);
      });
      setCheckedCalendars(newSet);
    } else {
      // Select all calendars
      const newSet = new Set(checkedCalendars);
      availableCalendars.forEach((calendar) => {
        newSet.add(calendar.id);
      });
      setCheckedCalendars(newSet);
    }
  };

  // Handle select all/none for athletes
  const handleAthleteSelectAll = (event) => {
    event.stopPropagation();

    if (allAthletesSelected) {
      // Unselect all athletes
      const newSet = new Set(checkedAthletes);
      availableAthletes.forEach((athlete) => {
        newSet.delete(athlete.id);
      });
      setCheckedAthletes(newSet);
    } else {
      // Select all athletes
      const newSet = new Set(checkedAthletes);
      availableAthletes.forEach((athlete) => {
        newSet.add(athlete.id);
      });
      setCheckedAthletes(newSet);
    }
  };

  // Handle search input change - adapted from SelectCalendarModal
  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);

    if (!rightToggled) {
      // Calendar search
      if (value.length > 0) {
        setIsUserSearching(true);
        const newCalendars = [];
        parentCalendars.forEach((parentCalendar) => {
          if (parentCalendar.name.toLowerCase().includes(value.toLowerCase())) {
            newCalendars.push(parentCalendar);
          }
          if (parentCalendar.childCalendars && parentCalendar.childCalendars.length) {
            parentCalendar.childCalendars.forEach((childCalendar) => {
              if (childCalendar.name.toLowerCase().includes(value.toLowerCase())) {
                newCalendars.push(childCalendar);
              }
            });
          }
        });
        setSearchedList(newCalendars);
      } else {
        setIsUserSearching(false);
        setSearchedList([]);
      }
    } else if (value.length > 0) {
      setIsUserSearchingAthletes(true);
      const newList = calendarSelectAthletes.filter(
        (item) => item.first.toLowerCase().includes(value.toLowerCase())
          || item.last.toLowerCase().includes(value.toLowerCase()),
      );
      if (!newList.length) {
        setSearchedAthleteList([{
          first: 'No Athletes Found',
          fake: true,
          last: '',
          pic: '',
        }]);
      } else {
        setSearchedAthleteList(newList);
      }
    } else {
      setIsUserSearchingAthletes(false);
      setSearchedAthleteList([]);
    }
  };

  // Handle checkbox change for calendar selection
  const handleCheckboxChange = (calendarId, isChecked) => {
    setCheckedCalendars((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(calendarId);
      } else {
        newSet.delete(calendarId);
      }
      return newSet;
    });
  };

  // Handle checkbox change for athlete selection
  const handleAthleteCheckboxChange = (athleteId, isChecked) => {
    setCheckedAthletes((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(athleteId);
      } else {
        newSet.delete(athleteId);
      }
      return newSet;
    });
  };

  // Options for placement select
  const placementOptions = [
    { value: 'bottom', label: 'Bottom of workout' },
    { value: 'top', label: 'Top of workout' },
  ];

  // Handler for toggle switch
  const handleToggleChange = () => {
    setClearExistingWorkouts(!clearExistingWorkouts);
  };

  // Helper functions for program library week/day selection
  const buildWeekDayStructure = (programItems) => {
    if (!programItems || typeof programItems !== 'object') return [];

    const weekMap = {};

    // programItems is an object where keys are indices and values are day objects
    Object.values(programItems).forEach((item) => {
      const weekNum = item.weekNumber;
      const dayNum = item.dayNumber;

      if (!weekMap[weekNum]) {
        weekMap[weekNum] = {
          weekNumber: weekNum,
          days: [],
        };
      }

      weekMap[weekNum].days.push({
        dayNumber: dayNum,
        dayInWeek: item.dayInWeek,
        exercises: item.exercises || [],
        title: item.title || `Day ${dayNum + 1}`,
      });
    });

    // Convert to array and sort
    return Object.values(weekMap).sort((a, b) => a.weekNumber - b.weekNumber);
  };

  const toggleProgramDay = (weekNumber, dayNumber, currentSelections) => {
    const key = `${weekNumber}-${dayNumber}`;

    // If clicking the already selected day, deselect it
    if (currentSelections.has(key)) {
      return new Set();
    }

    // Otherwise, clear all selections and select only this day
    return new Set([key]);
  };

  // Prepare the request payload for API submission
  const prepareSubmitData = () => {
    // Get array of selected days of week (0-6, where 0 is Sunday)
    const getSelectedDays = () => {
      const dayMap = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };

      return Object.entries(selectedDays)
        .filter(([, isSelected]) => isSelected)
        .map(([day]) => dayMap[day]);
    };

    // Program Library with pre-selected items - uses new endpoint
    if (programType === 'free-form-program' && modalData?.preSelectedItems) {
      // Extract source assignedIds from pre-selected items
      // Filter out presaved items (which have string IDs instead of numbers)
      const sourceAssignedIds = Object.values(modalData?.preSelectedItems)
        .map((item) => item.assignedId)
        .filter((id) => id !== undefined && id !== null && typeof id === 'number');

      // Get week/day info from first pre-selected item to calculate sourceDayNumber
      const weekDayInfo = getWeekDayFromPreSelectedItems();

      const sourceDayNumber = weekDayInfo
        ? weekDayInfo.dayNumber
        : 0;

      // Get target day info from first selected paste destination
      const firstTargetKey = Array.from(pasteDestinationDays)[0];
      let targetDayNumber = 0;
      if (firstTargetKey) {
        const [, dayNum] = firstTargetKey.split('-').map(Number);
        targetDayNumber = dayNum;
      }

      const payload = {
        clearExisting: clearExistingWorkouts,
        copyType: 'individual-assignments',
        includePrescribedLoads: contentOptions.prescribedLoads,
        includeWorkoutInfo: contentOptions.workoutTitles,
        sourceAssignedIds,
        sourceDayNumber,
        targetDayNumber,
      };

      // Add targetOrderLocation top when clearExistingWorkouts is checked
      if (clearExistingWorkouts) {
        payload.targetOrderLocation = 'top';
      }

      // otherwise use value
      if (!clearExistingWorkouts) {
        payload.targetOrderLocation = placementOption.value;
      }

      return payload;
    }

    // Program Library without pre-selected items - uses original endpoint
    if (programType === 'free-form-program') {
      // Add exerTypes for content filtering
      const typeMap = {
        circuits: 'L',
        health: 'T',
        notes: 'R',
        rest: 'RT',
        warmUps: 'W',
        cooldowns: 'CD',
        questionnaires: 'S',
        sessions: 'BR',
        videos: 'V',
      };

      const exerTypes = [];
      Object.entries(contentOptions).forEach(([key, value]) => {
        if (value && typeMap[key]) {
          exerTypes.push(typeMap[key]);
        }
      });

      Object.entries(additionalOptions).forEach(([key, value]) => {
        if (value) {
          if (key === 'circuits2') exerTypes.push('C');
          if (key === 'health2') exerTypes.push('H');
          if (key === 'notes2') exerTypes.push('N');
        }
      });

      // For program library, use date-based parameters matching API schema
      const payload = {
        clearExisting: clearExistingWorkouts,
        copyType: dateMode === 'range'
          ? 'range'
          : (secondSlideDateMode === 'range' ? 'multiple' : 'single'),
        exerTypes: [...new Set(exerTypes)],
        includePrescribedLoads: contentOptions.prescribedLoads,
        includeWorkoutInfo: contentOptions.workoutTitles,
        sourceDateStart: moment(startDate).format('YYYY-MM-DD'),
        sourceDateEnd: moment(dateMode === 'single' ? startDate : endDate).format('YYYY-MM-DD'),
        targetDateStart: moment(secondSlideStartDate).format('YYYY-MM-DD'),
        targetDateEnd: moment(secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideEndDate).format('YYYY-MM-DD'),
      };

      // Only add targetOrderLocation if we're not clearing existing workouts
      if (!clearExistingWorkouts) {
        payload.targetOrderLocation = placementOption.value;
      }

      // Add source information based on current Redux selection
      if (selectedReduxCalendar?.id) {
        payload.sourceCalendarId = parseInt(selectedReduxCalendar.id, 10);
      }
      if (selectedReduxAthlete?.userId) {
        payload.sourceUserId = parseInt(selectedReduxAthlete.userId, 10);
      }

      // Add target information based on what's selected in slide 3
      if (!rightToggled) {
        // Calendar mode - use selected calendars from checkboxes
        const selectedCalendarIds = Array.from(checkedCalendars);
        if (selectedCalendarIds.length > 0) {
          payload.targetCalendarIds = selectedCalendarIds;
        }
      } else {
        // Athlete mode - use selected athletes from checkboxes
        const selectedAthleteIds = Array.from(checkedAthletes);
        if (selectedAthleteIds.length > 0) {
          payload.targetUserIds = selectedAthleteIds;
        }
      }

      return payload;
    }

    // Calendar mode with pre-selected items - uses assignment-based endpoint
    if (modalData?.preSelectedItems && Object.keys(modalData?.preSelectedItems).length > 0) {
      // Extract source assignedIds from pre-selected items
      // Filter out presaved items (which have string IDs instead of numbers)
      const sourceAssignedIds = Object.values(modalData?.preSelectedItems)
        .map((item) => item.assignedId)
        .filter((id) => id !== undefined && id !== null && typeof id === 'number');

      // For calendar mode, we don't have day numbers, so we use dates instead
      // The backend will handle the date-to-day conversion
      const targetStartDate = secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideStartDate;
      const targetEndDate = dateMode === 'range'
        ? moment(secondSlideStartDate).add(moment(endDate).diff(moment(startDate), 'days'), 'days').toDate()
        : (secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideEndDate);

      const payload = {
        clearExisting: clearExistingWorkouts,
        copyType: 'individual-assignments',
        includePrescribedLoads: contentOptions.prescribedLoads,
        includeWorkoutInfo: contentOptions.workoutTitles,
        sourceAssignedIds,
        sourceDateStart: moment(startDate).format('YYYY-MM-DD'),
        sourceDateEnd: moment(endDate).format('YYYY-MM-DD'),
        targetDateStart: moment(targetStartDate).format('YYYY-MM-DD'),
        targetDateEnd: targetEndDate ? moment(targetEndDate).format('YYYY-MM-DD') : '',
        targetDays: secondSlideDateMode === 'range' ? getSelectedDays() : [],
      };

      // Only add targetOrderLocation if we're not clearing existing workouts
      if (!clearExistingWorkouts) {
        payload.targetOrderLocation = placementOption.value;
      }

      // Add source information based on current Redux selection
      if (selectedReduxCalendar?.id) {
        payload.sourceCalendarId = parseInt(selectedReduxCalendar.id, 10);
      }
      if (selectedReduxAthlete?.userId) {
        payload.sourceUserId = parseInt(selectedReduxAthlete.userId, 10);
      }

      // Add target information based on what's selected in slide 3
      if (!rightToggled) {
        // Calendar mode - use selected calendars from checkboxes
        const selectedCalendarIds = Array.from(checkedCalendars);
        if (selectedCalendarIds.length > 0) {
          payload.targetCalendarIds = selectedCalendarIds;
        }
      } else {
        // Athlete mode - use selected athletes from checkboxes
        const selectedAthleteIds = Array.from(checkedAthletes);
        if (selectedAthleteIds.length > 0) {
          payload.targetUserIds = selectedAthleteIds;
        }
      }

      return payload;
    }

    // Original Calendar mode
    // Map content options to exerTypes codes based on API documentation
    const mapContentOptionsToExerTypes = () => {
      const typeMap = {
        circuits: 'L', // Lifts (based on UI label)
        health: 'T', // Tags (based on UI label)
        notes: 'R', // SAQ+C (based on UI label)
        rest: 'RT', // Rest (based on UI label)
        warmUps: 'W', // Warmup
        cooldowns: 'CD', // Cooldown
        questionnaires: 'S', // Progression
        sessions: 'BR', // Session Break
        videos: 'V', // Video
      };

      const exerTypes = [];

      // Map selected content options to exercise types
      Object.entries(contentOptions).forEach(([key, value]) => {
        if (value && typeMap[key]) {
          exerTypes.push(typeMap[key]);
        }
      });

      // Map additional options to exercise types
      Object.entries(additionalOptions).forEach(([key, value]) => {
        if (value) {
          if (key === 'circuits2') exerTypes.push('C'); // Circuits
          if (key === 'health2') exerTypes.push('H'); // Health
          if (key === 'notes2') exerTypes.push('N'); // Notes
        }
      });

      return [...new Set(exerTypes)]; // Remove duplicates
    };

    // Get array of selected calendar IDs from checkboxes
    const getSelectedCalendarIds = () => Array.from(checkedCalendars);

    // Get array of selected athlete IDs from checkboxes (convert to user IDs for API)
    const getSelectedAthleteIds = () => Array.from(checkedAthletes);

    // Format date in YYYY-MM-DD format
    const formatDate = (date) => moment(date).format('YYYY-MM-DD');

    // Determine copy type based on whether we have pre-selected items and date modes
    let copyType = 'single';

    // Check if we have pre-selected items (individual assignments)
    const hasPreSelectedItems = modalData?.preSelectedItems
      && Object.keys(modalData?.preSelectedItems).length > 0;

    if (hasPreSelectedItems) {
      // When copying specific selected assignments, use 'individual-assignments' type
      copyType = 'individual-assignments';
    } else if (dateMode === 'range' && secondSlideDateMode === 'single') {
      copyType = 'range'; // Range to single date
    } else if (dateMode === 'range' && secondSlideDateMode === 'range') {
      copyType = 'range'; // Range to range
    } else if (dateMode === 'single' && secondSlideDateMode === 'range') {
      copyType = 'multiple'; // Single to range
    }

    // Use second slide dates for target dates
    const targetStartDate = secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideStartDate;
    // Calculate targetEndDate based on source range when range is selected on first slide
    const targetEndDate = dateMode === 'range'
      ? moment(secondSlideStartDate).add(moment(endDate).diff(moment(startDate), 'days'), 'days').toDate()
      : (secondSlideDateMode === 'single' ? secondSlideStartDate : secondSlideEndDate);

    // Build the request payload matching the expected API structure
    const payload = {
      clearExisting: clearExistingWorkouts,
      copyType,
      exerTypes: mapContentOptionsToExerTypes(), // Always required
      includePrescribedLoads: contentOptions.prescribedLoads,
      includeWorkoutInfo: contentOptions.workoutTitles,
      sourceDateEnd: formatDate(dateMode === 'single' ? startDate : endDate),
      sourceDateStart: formatDate(startDate),
      targetDateStart: formatDate(targetStartDate),
      targetDateEnd: targetEndDate ? formatDate(targetEndDate) : '',
      targetDays: (copyType === 'multiple' || (copyType === 'individual-assignments' && secondSlideDateMode === 'range'))
        ? getSelectedDays()
        : [],
    };

    // Only add targetOrderLocation if we're not clearing existing workouts
    if (!clearExistingWorkouts) {
      payload.targetOrderLocation = placementOption.value;
    }

    // Add source information based on current Redux selection
    if (selectedReduxCalendar?.id) {
      payload.sourceCalendarId = parseInt(selectedReduxCalendar.id, 10);
    }
    if (selectedReduxAthlete?.userId) {
      payload.sourceUserId = parseInt(selectedReduxAthlete.userId, 10);
    }

    // Add target information based on what's selected in slide 3
    if (!rightToggled) {
      // Calendar mode - use selected calendars from checkboxes
      const selectedCalendarIds = getSelectedCalendarIds();
      if (selectedCalendarIds.length > 0) {
        payload.targetCalendarIds = selectedCalendarIds;
      }
    } else {
      // Athlete mode - use selected athletes from checkboxes
      const selectedAthleteIds = getSelectedAthleteIds();
      if (selectedAthleteIds.length > 0) {
        payload.targetUserIds = selectedAthleteIds;
      }
    }

    return payload;
  };

  // Handle final submit
  const handleFinish = () => {
    // Prepare the data for submission
    const requestData = prepareSubmitData();

    // Check if this is the new free-form-program with pre-selected items flow
    const isNewProgramLibraryFlow = programType === 'free-form-program'
      && modalData?.preSelectedItems;

    if (isNewProgramLibraryFlow) {
      // Use new endpoint for program library with pre-selected items
      dispatch(copyWorkoutItems(
        currentUser.accountCode,
        requestData,
        nonDateBasedProgramItemsProgramId,
      ));
    } else {
      // Use existing endpoint
      dispatch(copyWorkoutItems(currentUser.accountCode, requestData));
    }

    // Close the modal
    closeModal();
  };

  // Breadcrumb navigation handler
  const handleBreadcrumbClick = (step) => {
    // Only allow clicking on completed steps or current step
    if (step <= currentSlide) {
      setHideOverflow(true);
      setCurrentSlide(step);
    }
  };

  // Breadcrumb component - show 1, 2, or 3 steps based on mode
  const renderBreadcrumb = () => {
    // Program library with pre-selected items has only 1 step (paste destination)
    if (programType === 'free-form-program' && modalData?.preSelectedItems) {
      const steps = [
        { number: 1, text: 'Paste', slideIndex: 0 },
      ];

      return (
        <BreadcrumbContainer>
          {steps.map((step, index) => {
            const isActive = step.slideIndex === currentSlide;
            const isCompleted = step.slideIndex < currentSlide;
            const isClickable = step.slideIndex <= currentSlide;

            return (
              <React.Fragment key={step.number}>
                <BreadcrumbStep
                  aria-current={isActive ? 'step' : undefined}
                  clickable={isClickable}
                  disabled={!isClickable}
                  onClick={() => handleBreadcrumbClick(step.slideIndex)}
                  /* Mouse-only: the BACK button already gives keyboard users this path, and
                     tabbing past the last control should not wrap up into the breadcrumb. */
                  tabIndex={-1}
                  type='button'
                >
                  <BreadcrumbNumber
                    active={isActive}
                    completed={isCompleted}
                  >
                    {isCompleted === true ? (
                      <IcomoonReact
                        aria-hidden='true'
                        iconSet={iconSet}
                        size={20}
                        icon='checkmark'
                        color={colors.white}
                      />
                    ) : (
                      step.number
                    )}
                  </BreadcrumbNumber>
                  <BreadcrumbText
                    active={isActive}
                    completed={isCompleted}
                  >
                    {step.text}
                  </BreadcrumbText>
                </BreadcrumbStep>
                {index < steps.length - 1 && (
                  <BreadcrumbArrow>
                    <IcomoonReact
                      iconSet={iconSet}
                      icon='gs-arrow-down'
                      size={12}
                      color={colors.textSecondary}
                    />
                  </BreadcrumbArrow>
                )}
              </React.Fragment>
            );
          })}
        </BreadcrumbContainer>
      );
    }

    // Program library without pre-selected items has 2 steps (no calendar selection slide)
    if (programType === 'free-form-program') {
      const steps = [
        { number: 1, text: 'Workout & Item Selection', slideIndex: 0 },
        { number: 2, text: 'Paste', slideIndex: 1 },
      ];

      return (
        <BreadcrumbContainer>
          {steps.map((step, index) => {
            const isActive = step.slideIndex === currentSlide;
            const isCompleted = step.slideIndex < currentSlide;
            const isClickable = step.slideIndex <= currentSlide;

            return (
              <React.Fragment key={step.number}>
                <BreadcrumbStep
                  aria-current={isActive ? 'step' : undefined}
                  clickable={isClickable}
                  disabled={!isClickable}
                  onClick={() => handleBreadcrumbClick(step.slideIndex)}
                  /* Mouse-only: the BACK button already gives keyboard users this path, and
                     tabbing past the last control should not wrap up into the breadcrumb. */
                  tabIndex={-1}
                  type='button'
                >
                  <BreadcrumbNumber
                    active={isActive}
                    completed={isCompleted}
                  >
                    {isCompleted === true ? (
                      <IcomoonReact
                        aria-hidden='true'
                        iconSet={iconSet}
                        size={20}
                        icon='checkmark'
                        color={colors.white}
                      />
                    ) : (
                      step.number
                    )}
                  </BreadcrumbNumber>
                  <BreadcrumbText
                    active={isActive}
                    completed={isCompleted}
                  >
                    {step.text}
                  </BreadcrumbText>
                </BreadcrumbStep>
                {index < steps.length - 1 && (
                  <BreadcrumbArrow>
                    <IcomoonReact
                      iconSet={iconSet}
                      icon='gs-arrow-down'
                      size={12}
                      color={colors.textSecondary}
                    />
                  </BreadcrumbArrow>
                )}
              </React.Fragment>
            );
          })}
        </BreadcrumbContainer>
      );
    }

    // Original calendar breadcrumbs
    const steps = modalData?.skipFirstSlide
      ? [
        { number: 1, text: 'Calendar Selection', slideIndex: 0 },
        { number: 2, text: secondSlideDateMode === 'range' ? 'Select Range to Paste' : 'Select Date to Paste', slideIndex: 1 },
      ]
      : [
        { number: 1, text: dateMode === 'range' ? 'Select Copy Range' : 'Select Copy Date', slideIndex: 0 },
        { number: 2, text: 'Calendar Selection', slideIndex: 1 },
        { number: 3, text: secondSlideDateMode === 'range' ? 'Select Target Range' : 'Select Target Date', slideIndex: 2 },
      ];

    return (
      <BreadcrumbContainer>
        {steps.map((step, index) => {
          const isActive = step.slideIndex === currentSlide;
          const isCompleted = step.slideIndex < currentSlide;
          const isClickable = step.slideIndex <= currentSlide;

          return (
            <React.Fragment key={step.number}>
              <BreadcrumbStep
                aria-current={isActive ? 'step' : undefined}
                clickable={isClickable}
                disabled={!isClickable}
                onClick={() => handleBreadcrumbClick(step.slideIndex)}
                /* Mouse-only: the BACK button already gives keyboard users this path, and
                   tabbing past the last control should not wrap up into the breadcrumb. */
                tabIndex={-1}
                type='button'
              >
                <BreadcrumbNumber
                  active={isActive}
                  completed={isCompleted}
                >
                  {isCompleted ? (
                    <IcomoonReact
                      aria-hidden='true'
                      iconSet={iconSet}
                      size={20}
                      icon='checkmark'
                      color={colors.white}
                    />
                  ) : (
                    step.number
                  )}
                </BreadcrumbNumber>
                <BreadcrumbText
                  active={isActive}
                  completed={isCompleted}
                >
                  {step.text}
                </BreadcrumbText>
              </BreadcrumbStep>
              {index < steps.length - 1 && (
                <BreadcrumbArrow>
                  <IcomoonReact
                    iconSet={iconSet}
                    icon='gs-arrow-down'
                    size={12}
                    color={colors.textSecondary}
                  />
                </BreadcrumbArrow>
              )}
            </React.Fragment>
          );
        })}
      </BreadcrumbContainer>
    );
  };

  return (
    <ModalContainer
      data-nav-mode='mouse'
      hideOverflow={hideOverflow}
      onKeyDown={handleCheckboxEnter}
      ref={modalContainerRef}
    >
      <ResizeObserver
        onResize={() => {
          setIsResizing(true);
          // Reset resizing state after animation would have completed
          setTimeout(() => {
            setIsResizing(false);
          }, 300);
        }}
      />

      {renderBreadcrumb()}

      {!modalData?.skipFirstSlide && showSlide1 && (
        <animated.div style={slide1Animation}>
          <div ref={slide1Ref} style={{ width: '100%' }} tabIndex={-1}>

            {programType === 'free-form-program' ? (
              <>
                {/* Week/Day Selection for Program Library */}
                <WeekDayContainer>
                  {buildWeekDayStructure(nonDateBasedProgramItems).map((week) => (
                    <WeekRow key={week.weekNumber}>
                      <WeekCheckboxRow>
                        <WeekLabel>
                          Week
                          {' '}
                          {week.weekNumber}
                        </WeekLabel>
                      </WeekCheckboxRow>

                      {week.days.map((day) => (
                        <DayCheckboxRow key={`${week.weekNumber}-${day.dayNumber}`}>
                          <ThemeProvider theme={checkboxTheme}>
                            <StyledCheckbox
                              checked={selectedProgramDays.has(
                                `${week.weekNumber}-${day.dayNumber}`,
                              )}
                              onChange={() => {
                                setSelectedProgramDays(
                                  toggleProgramDay(
                                    week.weekNumber,
                                    day.dayNumber,
                                    selectedProgramDays,
                                  ),
                                );
                              }}
                              icon={<CustomUncheckedIcon />}
                              checkedIcon={<CustomCheckedIcon />}
                              color='default'
                              id={`day-${week.weekNumber}-${day.dayNumber}`}
                            />
                          </ThemeProvider>
                          <DayLabel htmlFor={`day-${week.weekNumber}-${day.dayNumber}`}>
                            Day
                            {' '}
                            {day.dayInWeek}
                          </DayLabel>
                        </DayCheckboxRow>
                      ))}
                    </WeekRow>
                  ))}
                </WeekDayContainer>

              </>
            ) : (
              <>
                {/* Original Date-based selection for Calendar */}
                <TabsContainer aria-label='Copy date mode' role='tablist'>
                  <DateModeTab
                    active={dateMode === 'single'}
                    aria-controls='copy-range-date-section'
                    aria-selected={dateMode === 'single'}
                    id='copy-range-tab-single'
                    onClick={() => setDateMode('single')}
                    onKeyDown={(e) => handleDateModeTabKeyDown(e, setDateMode, 'single', 'copy-range-date-section')}
                    role='tab'
                    type='button'
                  >
                    <TabLabel>Single Date</TabLabel>
                  </DateModeTab>
                  <DateModeTab
                    active={dateMode === 'range'}
                    aria-controls='copy-range-date-section'
                    aria-selected={dateMode === 'range'}
                    id='copy-range-tab-range'
                    onClick={() => setDateMode('range')}
                    onKeyDown={(e) => handleDateModeTabKeyDown(e, setDateMode, 'range', 'copy-range-date-section')}
                    role='tab'
                    type='button'
                  >
                    <TabLabel>Date Range</TabLabel>
                  </DateModeTab>
                </TabsContainer>

                <SelectDateSection
                  aria-labelledby={dateMode === 'single' ? 'copy-range-tab-single' : 'copy-range-tab-range'}
                  id='copy-range-date-section'
                  role='tabpanel'
                >
                  {dateMode === 'single' ? (
                    <PickerFocusWrap
                      onKeyDown={handleDatePickerKeyDown}
                      onKeyDownCapture={handleDatePickerKeyDownCapture}
                    >
                      <TBDatePicker
                        activeDate={startDateString}
                        setActiveDate={handleStartDateChange}
                        additionalStyles={{
                          margin: '0',
                          maxWidth: '100%',
                        }}
                        showDropShadow={false}
                        showIcon
                        maxDate={null}
                      />
                    </PickerFocusWrap>
                  ) : (
                    <DatePickerRow>
                      <TextAndPicker
                        onKeyDown={handleDatePickerKeyDown}
                        onKeyDownCapture={handleDatePickerKeyDownCapture}
                      >
                        <DateLabel>Start Date</DateLabel>
                        <PickerFocusWrap addTopMargin>
                          <TBDatePicker
                            activeDate={startDateString}
                            setActiveDate={handleStartDateChange}
                            additionalStyles={{
                              margin: '0',
                              maxWidth: '100%',
                            }}
                            showDropShadow={false}
                            showIcon
                            maxDate={null}
                          />
                        </PickerFocusWrap>
                      </TextAndPicker>

                      <TextAndPicker
                        onKeyDown={handleDatePickerKeyDown}
                        onKeyDownCapture={handleDatePickerKeyDownCapture}
                      >
                        <DateLabel>End Date</DateLabel>
                        <PickerFocusWrap addTopMargin>
                          <TBDatePicker
                            activeDate={endDateString}
                            setActiveDate={handleEndDateChange}
                            additionalStyles={{
                              margin: '0',
                              maxWidth: '100%',
                            }}
                            showDropShadow={false}
                            showIcon
                            maxDate={null}
                            minDate={startDate}
                          />
                        </PickerFocusWrap>
                      </TextAndPicker>
                    </DatePickerRow>
                  )}
                </SelectDateSection>

                {dateRangeError && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    width: '100%',
                  }}
                  >
                    <Text color='#E53935' fontSize='12px' style={{ marginTop: '8px' }}>{dateRangeError}</Text>
                  </div>
                )}
              </>
            )}

            <CheckboxRow>
              <CheckboxContainer>
                <ThemeProvider theme={checkboxTheme}>
                  <StyledCheckbox
                    checked={contentOptions.prescribedLoads}
                    onChange={() => toggleContentOption('prescribedLoads')}
                    icon={<CustomUncheckedIcon />}
                    checkedIcon={<CustomCheckedIcon />}
                    color='default'
                    id='prescribedLoads'
                  />
                </ThemeProvider>
                <CheckboxLabel htmlFor='prescribedLoads'>Prescribed loads</CheckboxLabel>
              </CheckboxContainer>

              <CheckboxContainer>
                <ThemeProvider theme={checkboxTheme}>
                  <StyledCheckbox
                    checked={contentOptions.workoutTitles}
                    onChange={() => toggleContentOption('workoutTitles')}
                    icon={<CustomUncheckedIcon />}
                    checkedIcon={<CustomCheckedIcon />}
                    color='default'
                    id='workoutTitles'
                  />
                </ThemeProvider>
                <CheckboxLabel htmlFor='workoutTitles'>Include workout titles and colors</CheckboxLabel>
              </CheckboxContainer>
            </CheckboxRow>

            <AdditionalOptionsToggle
              aria-controls='copy-additional-options'
              aria-expanded={showAdditionalOptions}
              onClick={handleAdditionalOptionsToggle}
              ref={additionalOptionsToggleRef}
              type='button'
            >
              <span>Additional Options</span>
              <ChevronIcon expanded={showAdditionalOptions}>
                <svg aria-hidden='true' width='12' height='8' viewBox='0 0 12 8' fill='none'>
                  <path
                    d='M1 1.5L6 6.5L11 1.5'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </ChevronIcon>
            </AdditionalOptionsToggle>

            <ItemsToCopySection expanded={showAdditionalOptions} id='copy-additional-options'>
              <ItemsToCopyTitle>Items</ItemsToCopyTitle>
              <OptionsGrid>
                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={!!contentOptions.circuits}
                      onChange={() => toggleContentOption('circuits')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='circuits-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='circuits-slide1'>Lifts</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={contentOptions.health}
                      onChange={() => toggleContentOption('health')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='health-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='health-slide1'>Tags</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={contentOptions.notes}
                      onChange={() => toggleContentOption('notes')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='notes-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='notes-slide1'>SAQ+C</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={contentOptions.videos}
                      onChange={() => toggleContentOption('videos')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='videos-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='videos-slide1'>Videos</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={contentOptions.rest}
                      onChange={() => toggleContentOption('rest')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='rest-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='rest-slide1'>Rest</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={contentOptions.warmUps}
                      onChange={() => toggleContentOption('warmUps')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='warmUps-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='warmUps-slide1'>Warm Ups</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={contentOptions.cooldowns}
                      onChange={() => toggleContentOption('cooldowns')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='cooldowns-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='cooldowns-slide1'>Cooldowns</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={contentOptions.questionnaires}
                      onChange={() => toggleContentOption('questionnaires')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='questionnaires-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='questionnaires-slide1'>Questionnaires</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={contentOptions.sessions}
                      onChange={() => toggleContentOption('sessions')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='sessions-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='sessions-slide1'>Sessions</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={additionalOptions.circuits2}
                      onChange={() => toggleAdditionalOption('circuits2')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='circuits2-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='circuits2-slide1'>Circuits</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={additionalOptions.health2}
                      onChange={() => toggleAdditionalOption('health2')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='health2-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='health2-slide1'>Health</CheckboxLabel>
                </CheckboxContainer>

                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={additionalOptions.notes2}
                      onChange={() => toggleAdditionalOption('notes2')}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='notes2-slide1'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='notes2-slide1'>Notes</CheckboxLabel>
                </CheckboxContainer>
              </OptionsGrid>
            </ItemsToCopySection>

            <ButtonsContainer data-modal-footer>
              <PrimaryButton
                disabled={isMissingCopyDate}
                onClick={handleNext}
                type='button'
              >
                NEXT
              </PrimaryButton>
              <SecondaryButton onClick={closeModal} type='button'>BACK</SecondaryButton>
            </ButtonsContainer>
          </div>
        </animated.div>
      )}

      {showSlide3 && (
        <animated.div style={slide3Animation}>
          <div ref={slide3Ref} style={{ width: '100%' }} tabIndex={-1}>
            {programType === 'free-form-program' ? (
              <>
                {/* Program Library Paste Selection */}
                <ProgramNameDisplay>{programName}</ProgramNameDisplay>

                {/* Show stats for pre-selected items */}
                {modalData?.preSelectedItems && (
                  <StatsContainer>
                    <span>
                      Selected Items:
                      {' '}
                      {Object.keys(modalData?.preSelectedItems).length}
                    </span>
                  </StatsContainer>
                )}

                <WeekDayContainer>
                  {buildWeekDayStructure(nonDateBasedProgramItems).map((week) => (
                    <WeekRow key={week.weekNumber}>
                      <WeekCheckboxRow>
                        <WeekLabel>
                          Week
                          {' '}
                          {week.weekNumber}
                        </WeekLabel>
                      </WeekCheckboxRow>

                      {week.days.map((day) => (
                        <DayCheckboxRow key={`${week.weekNumber}-${day.dayNumber}`}>
                          <ThemeProvider theme={checkboxTheme}>
                            <StyledCheckbox
                              checked={pasteDestinationDays.has(
                                `${week.weekNumber}-${day.dayNumber}`,
                              )}
                              onChange={() => {
                                setPasteDestinationDays(
                                  toggleProgramDay(
                                    week.weekNumber,
                                    day.dayNumber,
                                    pasteDestinationDays,
                                  ),
                                );
                              }}
                              icon={<CustomUncheckedIcon />}
                              checkedIcon={<CustomCheckedIcon />}
                              color='default'
                              id={`paste-day-${week.weekNumber}-${day.dayNumber}`}
                            />
                          </ThemeProvider>
                          <DayLabel htmlFor={`paste-day-${week.weekNumber}-${day.dayNumber}`}>
                            Day
                            {' '}
                            {day.dayInWeek}
                          </DayLabel>
                        </DayCheckboxRow>
                      ))}
                    </WeekRow>
                  ))}
                </WeekDayContainer>

                <BottomOptionsContainer>
                  <OptionsColumn>
                    <OptionLabel>Placement</OptionLabel>
                    <PlacementSelect
                      options={placementOptions}
                      value={placementOption}
                      onChange={setPlacementOption}
                    />
                  </OptionsColumn>
                  <OptionsColumn>
                    <OptionLabel>Existing Workouts</OptionLabel>
                    <CheckboxContainer>
                      <ThemeProvider theme={checkboxTheme}>
                        <StyledCheckbox
                          checked={clearExistingWorkouts}
                          onChange={handleToggleChange}
                          icon={<CustomUncheckedIcon />}
                          checkedIcon={<CustomCheckedIcon />}
                          color='default'
                          id='clearExistingWorkouts-slide2'
                        />
                      </ThemeProvider>
                      <CheckboxLabel htmlFor='clearExistingWorkouts-slide2'>
                        Clear current workouts
                      </CheckboxLabel>
                    </CheckboxContainer>
                  </OptionsColumn>
                </BottomOptionsContainer>

                <ButtonsContainer data-modal-footer>
                  <PrimaryButton onClick={handleFinish} type='button'>PASTE</PrimaryButton>
                  <SecondaryButton onClick={handleBack} type='button'>BACK</SecondaryButton>
                </ButtonsContainer>
              </>
            ) : (
              <>
                {/* Only show tabs if single date was selected on slide 1 */}
                {dateMode === 'single' && (
                  <TabsContainer aria-label='Paste date mode' role='tablist'>
                    <DateModeTab
                      active={secondSlideDateMode === 'single'}
                      aria-controls='copy-target-date-section'
                      aria-selected={secondSlideDateMode === 'single'}
                      id='copy-target-tab-single'
                      onClick={() => setSecondSlideDateMode('single')}
                      onKeyDown={(e) => handleDateModeTabKeyDown(e, setSecondSlideDateMode, 'single', 'copy-target-date-section')}
                      role='tab'
                      type='button'
                    >
                      <TabLabel>Single Date</TabLabel>
                    </DateModeTab>
                    <DateModeTab
                      active={secondSlideDateMode === 'range'}
                      aria-controls='copy-target-date-section'
                      aria-selected={secondSlideDateMode === 'range'}
                      id='copy-target-tab-range'
                      onClick={() => setSecondSlideDateMode('range')}
                      onKeyDown={(e) => handleDateModeTabKeyDown(e, setSecondSlideDateMode, 'range', 'copy-target-date-section')}
                      role='tab'
                      type='button'
                    >
                      <TabLabel>Date Range</TabLabel>
                    </DateModeTab>
                  </TabsContainer>
                )}

                <SelectDateSection id='copy-target-date-section'>
                  {secondSlideDateMode === 'single' ? (
                    dateMode === 'range' ? (
                      <TextAndPicker
                        fullWidth
                        onKeyDown={handleDatePickerKeyDown}
                        onKeyDownCapture={handleDatePickerKeyDownCapture}
                      >
                        <DateLabel addTopMargin>Start Date</DateLabel>
                        <PickerFocusWrap addTopMargin>
                          <TBDatePicker
                            activeDate={secondSlideStartDateString}
                            setActiveDate={handleSecondSlideStartDateChange}
                            additionalStyles={{
                              margin: '0',
                              maxWidth: '100%',
                            }}
                            showDropShadow={false}
                            showIcon
                            maxDate={null}
                          />
                        </PickerFocusWrap>
                      </TextAndPicker>
                    ) : (
                      <PickerFocusWrap
                        onKeyDown={handleDatePickerKeyDown}
                        onKeyDownCapture={handleDatePickerKeyDownCapture}
                      >
                        <TBDatePicker
                          activeDate={secondSlideStartDateString}
                          setActiveDate={handleSecondSlideStartDateChange}
                          additionalStyles={{
                            margin: '0',
                            maxWidth: '100%',
                          }}
                          showDropShadow={false}
                          showIcon
                          maxDate={null}
                        />
                      </PickerFocusWrap>
                    )
                  ) : (
                    <DatePickerRow>
                      <TextAndPicker
                        onKeyDown={handleDatePickerKeyDown}
                        onKeyDownCapture={handleDatePickerKeyDownCapture}
                      >
                        <DateLabel>Start Date</DateLabel>
                        <PickerFocusWrap addTopMargin>
                          <TBDatePicker
                            activeDate={secondSlideStartDateString}
                            setActiveDate={handleSecondSlideStartDateChange}
                            additionalStyles={{
                              margin: '0',
                              maxWidth: '100%',
                            }}
                            showDropShadow={false}
                            showIcon
                            maxDate={null}
                          />
                        </PickerFocusWrap>
                      </TextAndPicker>

                      <TextAndPicker
                        onKeyDown={handleDatePickerKeyDown}
                        onKeyDownCapture={handleDatePickerKeyDownCapture}
                      >
                        <DateLabel>End Date</DateLabel>
                        <PickerFocusWrap addTopMargin>
                          <TBDatePicker
                            activeDate={secondSlideEndDateString}
                            setActiveDate={handleSecondSlideEndDateChange}
                            additionalStyles={{
                              margin: '0',
                              maxWidth: '100%',
                            }}
                            showDropShadow={false}
                            showIcon
                            maxDate={null}
                            minDate={secondSlideStartDate}
                          />
                        </PickerFocusWrap>
                      </TextAndPicker>
                    </DatePickerRow>
                  )}
                </SelectDateSection>

                {/* Show day selection when in range mode */}
                {secondSlideDateMode === 'range' && (
                  <SelectDateSection>
                    <SelectDaysLabel>Select Days</SelectDaysLabel>
                    <DaySelectors>
                      <DaySelector
                        aria-label='Monday'
                        aria-pressed={!!selectedDays.monday}
                        selected={selectedDays.monday}
                        onClick={() => toggleDay('monday')}
                        type='button'
                      >
                        M
                      </DaySelector>
                      <DaySelector
                        aria-label='Tuesday'
                        aria-pressed={!!selectedDays.tuesday}
                        selected={selectedDays.tuesday}
                        onClick={() => toggleDay('tuesday')}
                        type='button'
                      >
                        T
                      </DaySelector>
                      <DaySelector
                        aria-label='Wednesday'
                        aria-pressed={!!selectedDays.wednesday}
                        selected={selectedDays.wednesday}
                        onClick={() => toggleDay('wednesday')}
                        type='button'
                      >
                        W
                      </DaySelector>
                      <DaySelector
                        aria-label='Thursday'
                        aria-pressed={!!selectedDays.thursday}
                        selected={selectedDays.thursday}
                        onClick={() => toggleDay('thursday')}
                        type='button'
                      >
                        Th
                      </DaySelector>
                      <DaySelector
                        aria-label='Friday'
                        aria-pressed={!!selectedDays.friday}
                        selected={selectedDays.friday}
                        onClick={() => toggleDay('friday')}
                        type='button'
                      >
                        F
                      </DaySelector>
                      <DaySelector
                        aria-label='Saturday'
                        aria-pressed={!!selectedDays.saturday}
                        selected={selectedDays.saturday}
                        onClick={() => toggleDay('saturday')}
                        type='button'
                      >
                        Sa
                      </DaySelector>
                      <DaySelector
                        aria-label='Sunday'
                        aria-pressed={!!selectedDays.sunday}
                        selected={selectedDays.sunday}
                        onClick={() => toggleDay('sunday')}
                        type='button'
                      >
                        Su
                      </DaySelector>
                    </DaySelectors>
                  </SelectDateSection>
                )}

                <DayCountText>
                  {secondSlideDateMode === 'range' && (
                    `Selected days: ${Object.values(selectedDays).filter(Boolean).length} of 7`
                  )}
                </DayCountText>

                <ButtonsContainer data-modal-footer>
                  <PrimaryButton
                    onClick={handleFinish}
                    disabled={isPasteDisabled}
                    style={{
                      opacity: isPasteDisabled ? 0.5 : 1,
                      cursor: isPasteDisabled ? 'not-allowed' : 'pointer',
                    }}
                    type='button'
                  >
                    PASTE
                  </PrimaryButton>
                  <SecondaryButton onClick={handleBack} type='button'>BACK</SecondaryButton>
                </ButtonsContainer>
              </>
            )}
          </div>
        </animated.div>
      )}

      {showSlide2 && (
        <animated.div style={slide2Animation}>
          <div ref={slide2Ref} style={{ width: '100%' }} tabIndex={-1}>
            <ToggleContainer aria-label='Calendar source' role='tablist'>
              <LeftToggle
                aria-selected={!rightToggled}
                onClick={() => setRightToggled(false)}
                role='tab'
                showLine={!rightToggled}
                type='button'
              >
                <TabLabel>Calendars</TabLabel>
              </LeftToggle>
              <RightToggle
                aria-selected={rightToggled}
                onClick={() => setRightToggled(true)}
                role='tab'
                showLine={rightToggled}
                type='button'
              >
                <TabLabel>Athlete Calendars</TabLabel>
              </RightToggle>
            </ToggleContainer>

            <SearchBarContainer>
              <IconContainer>
                <IcomoonReact
                  iconSet={iconSet}
                  size={17}
                  icon='magnifying-glass'
                  color={colors.textSecondary}
                />
              </IconContainer>
              <SearchBar
                placeholder={`Search ${!rightToggled ? 'Calendars' : 'Athletes'}`}
                value={searchValue}
                onChange={handleSearchChange}
              />
            </SearchBarContainer>

            {!rightToggled ? (
              <>
                <SelectedCalendarCheckboxContainer
                  isChecked={allCalendarsSelected}
                  itemsAreSelected={checkedCalendars.size > 0}
                >
                  <input
                    ref={(el) => {
                      if (el) {
                        // eslint-disable-next-line no-param-reassign
                        el.indeterminate = someCalendarsSelected;
                      }
                    }}
                    className='checkbox-input'
                    type='checkbox'
                    name='selectedCalendars'
                    checked={allCalendarsSelected}
                    onClick={handleCalendarSelectAll}
                  />
                  <SelectionText hasSelection={checkedCalendars.size > 0}>
                    {`Selected (${checkedCalendars.size} calendars)`}
                  </SelectionText>
                  {checkedCalendars?.size < 1 && (
                  <Text fontSize='14px' color={colors.textError}>Please select at least 1 calendar</Text>
                  )}
                </SelectedCalendarCheckboxContainer>
                <CalendarsContainer
                  isLoading={isParentCalendarsLoading}
                  ref={calendarContainerRef}
                >
                  {isParentCalendarsLoading ? <Spinner /> : null}
                  {!isParentCalendarsLoading ? !isUserSearching
                    ? flattenedCalendars.map((calendar) => (
                      <SimpleCalendarItem
                        key={calendar.id}
                        calendar={calendar}
                        isSelected={calendar.id === selectedReduxCalendar?.id}
                        isChecked={checkedCalendars.has(calendar.id)}
                        onCheckboxChange={handleCheckboxChange}
                      />
                    ))
                    : searchedList.map((calendar) => (
                      <SimpleCalendarItem
                        key={calendar.id}
                        calendar={calendar}
                        isChecked={checkedCalendars.has(calendar.id)}
                        onCheckboxChange={handleCheckboxChange}
                      />
                    )) : null}
                </CalendarsContainer>
              </>
            ) : (
              <>
                <SelectedCalendarCheckboxContainer
                  isChecked={allAthletesSelected}
                  itemsAreSelected={checkedAthletes.size > 0}
                >
                  <input
                    ref={(el) => {
                      if (el) {
                        // eslint-disable-next-line no-param-reassign
                        el.indeterminate = someAthletesSelected;
                      }
                    }}
                    className='checkbox-input'
                    type='checkbox'
                    name='selectedAthletes'
                    checked={allAthletesSelected}
                    onClick={handleAthleteSelectAll}
                  />
                  <SelectionText hasSelection={checkedAthletes.size > 0}>
                    {`Selected (${checkedAthletes.size} athletes)`}
                  </SelectionText>
                  {checkedAthletes.size < 1 && (
                  <Text fontSize='14px' color={colors.textError}>Please select at least one athlete calendar</Text>
                  )}
                </SelectedCalendarCheckboxContainer>
                <SimpleCalendarSelectAthleteList
                  calendarSelectAthletes={calendarSelectAthletes}
                  searchedAthleteList={searchedAthleteList}
                  isUserSearching={isUserSearchingAthletes}
                  checkedAthletes={checkedAthletes}
                  onCheckboxChange={handleAthleteCheckboxChange}
                />
              </>
            )}

            <BottomOptionsContainer>
              <OptionsColumn>
                <OptionLabel>Placement</OptionLabel>
                <PlacementSelect
                  options={placementOptions}
                  value={placementOption}
                  onChange={setPlacementOption}
                />
              </OptionsColumn>
              <OptionsColumn>
                <OptionLabel>Existing Workouts</OptionLabel>
                <CheckboxContainer>
                  <ThemeProvider theme={checkboxTheme}>
                    <StyledCheckbox
                      checked={clearExistingWorkouts}
                      onChange={handleToggleChange}
                      icon={<CustomUncheckedIcon />}
                      checkedIcon={<CustomCheckedIcon />}
                      color='default'
                      id='clearExistingWorkouts'
                    />
                  </ThemeProvider>
                  <CheckboxLabel htmlFor='clearExistingWorkouts'>Clear existing workouts</CheckboxLabel>
                </CheckboxContainer>
              </OptionsColumn>
            </BottomOptionsContainer>

            <ButtonsContainer data-modal-footer>
              {!!preSavedItemAssignedIds?.length && dateMode !== 'range' && (
              <SelectedItemsWarning>
                <WarningIconWrapper>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={18}
                    icon='warning'
                    color='#F59E0B'
                  />
                </WarningIconWrapper>
                <Text>
                  {(savedItemAssignedIds?.length && preSavedItemAssignedIds?.length) ? 'Some of the items selected are not saved. These items will not be copied.' : 'Selected items are not saved. You must select at least 1 saved item to copy.'}
                </Text>
              </SelectedItemsWarning>
              )}
              <PrimaryButton
                disabled={
                  (savedItemAssignedIds?.length < 1 && modalData?.skipFirstSlide)
                  || (!rightToggled && checkedCalendars.size < 1)
                  || (rightToggled && checkedAthletes.size < 1)
                }
                onClick={handleNext}
                type='button'
              >
                NEXT
              </PrimaryButton>
              <SecondaryButton onClick={handleBack} type='button'>BACK</SecondaryButton>
            </ButtonsContainer>
          </div>
        </animated.div>
      )}
    </ModalContainer>
  );
};

CopyWorkoutModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.shape({
    weekStartDate: PropTypes.instanceOf(Date),
    weekEndDate: PropTypes.instanceOf(Date),
    isFromWeekView: PropTypes.bool,
  }),
};

CopyWorkoutModal.defaultProps = {
  modalData: null,
};

export default CopyWorkoutModal;
